"use client";

import { ReactNode } from "react";
import styles from "./ArticleSection.module.css";
import { getBlogAssetUrl } from "@/utils/common";

import {
  BlocksRenderer,
  type BlocksContent,
} from "@strapi/blocks-react-renderer";

type ImageFormats = {
  ext: string;
  hash: string;
  height: number;
  mime: string;
  name: string;
  path: null;
  size: number;
  sizeInBytes: number;
  url: string;
  width: number;
};

type ImageType = {
  __component: "article-blocks.article-image";
  id: number;
  description?: string;
  image: {
    alternativeText: string;
    caption: string;
    createdAt: string;
    documentId: string;
    ext: string;
    formats: {
      small: ImageFormats;
      medium: ImageFormats;
      thumbnail: ImageFormats;
    };
    hash: string;
    height: number;
    id: number;
    mime: string;
    name: string;
    previewUrl: string;
    provider: string;
    provider_metadata: string;
    publishedAt: string;
    size: number;
    updatedAt: string;
    url: string;
    width: number;
  } | null;
};

type SubtitleType = {
  id: number;
  subtitle: string;
  __component: "article-blocks.article-subtitle";
};

type TextType = {
  id: number;
  content: BlocksContent;
  __component: "article-blocks.article-text";
};

type ArticleItemType = TextType | ImageType | SubtitleType;

type ArticleDataType = {
  articleContent?: ArticleItemType[] | null;
  createdAt: string;
  documentId: string;
  id: number;
  locale: string;
  publishedAt: string;
  updatedAt: string;
};

export const ArticleSection = ({ data }: { data: ArticleDataType }) => {
  const article = data.articleContent ?? [];

  const renderArticle = (item: ArticleItemType): ReactNode => {
    switch (item.__component) {
      case "article-blocks.article-image":
        if (!item.image) {
          return item.description ? (
            <p className={styles["image-description"]}>{item.description}</p>
          ) : null;
        }

        return (
          <>
            <img
              src={getBlogAssetUrl(item.image.url)}
              width={item.image.width}
              height={item.image.height}
              alt={item.image.alternativeText ?? ""}
              style={{ paddingBottom: item.description ? "8px" : "16px" }}
            />
            {item.description && (
              <p className={styles["image-description"]}>{item.description}</p>
            )}
          </>
        );
      case "article-blocks.article-subtitle":
        return <h2 id={`subtitle-${item.id}`}>{item.subtitle}</h2>;
      case "article-blocks.article-text":
        return <BlocksRenderer content={item.content} />;
    }
    return null;
  };

  const subtitles = article.filter(
    (item): item is SubtitleType =>
      item.__component === "article-blocks.article-subtitle",
  );

  const handleClick = (id: number) => {
    const el = document.getElementById(`subtitle-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="uk-container">
      <div className={styles["article-container"]}>
        <div className={styles["article"]}>
          <div className={styles["content"]} id="blog-article">
            {subtitles?.length ? (
              <>
                <h2 style={{ padding: 0 }}>Содержание:</h2>
                <ul className={styles["content-list"]}>
                  {subtitles.map((item) => (
                    <li
                      key={item.id}
                      onClick={() => {
                        handleClick(item.id);
                      }}
                    >
                      {item.subtitle}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {article.map((item) => (
              <div key={item.id}>{renderArticle(item)}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
